/**
 * Copyright (c) 2026 Firstsource. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Firstsource.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Firstsource.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: [Developer Name / Date]
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import RunScriptForm from '../../components/runScript/RunScriptForm';
import { getCookie } from '../../components/login/cookies';
import { constants } from '../../utils/constants';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store/appStore';
import { setClearSaveJsonStatus } from '../../redux/slices/outputJson';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScriptStatus = 'Completed' | 'Running' | 'Failed';
type IconType = 'document' | 'video' | 'audio' | 'code' | 'checklist';
type ApiStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILURE';

interface ApiScriptRun {
  run_id: string;
  script_name: string;
  status: ApiStatus;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  triggered_by_name: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Config maps
// ---------------------------------------------------------------------------

const ICON_CONFIG: Record<IconType, { icon: React.ElementType; bg: string; color: string }> = {
  document: { icon: DescriptionOutlinedIcon, bg: '#E3F2FD', color: '#1565C0' },
  video: { icon: OndemandVideoIcon, bg: '#F3E5F5', color: '#6A1B9A' },
  audio: { icon: AudiotrackIcon, bg: '#E0F7FA', color: '#00838F' },
  code: { icon: CodeIcon, bg: '#FFF3E0', color: '#E65100' },
  checklist: { icon: PlaylistAddCheckIcon, bg: '#E0F2F1', color: '#00695C' },
};

const STATUS_CONFIG: Record<ScriptStatus, { bg: string; color: string; icon: React.ElementType; label: string }> = {
  Completed: { bg: '#E8F5E9', color: '#2E7D32', icon: CheckCircleOutlineIcon, label: 'Completed' },
  Running: { bg: '#FFF3E0', color: '#E65100', icon: HourglassEmptyIcon, label: 'Running' },
  Failed: { bg: '#FFEBEE', color: '#C62828', icon: ErrorOutlineIcon, label: 'Failed' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const mapApiStatus = (s: ApiStatus): ScriptStatus => {
  if (s === 'SUCCESS') return 'Completed';
  if (s === 'FAILURE') return 'Failed';
  return 'Running';
};

const deriveIconType = (scriptName: string): IconType => {
  const lower = scriptName.toLowerCase();
  if (lower.includes('audio') || lower.includes('transcription')) return 'audio';
  if (lower.includes('video')) return 'video';
  if (lower.includes('image') || lower.includes('ocr') || lower.includes('document')) return 'document';
  if (lower.includes('pii') || lower.includes('detection') || lower.includes('checklist')) return 'checklist';
  return 'code';
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatRunDate = (dateStr: string | null, fallback: string): string => {
  const d = new Date(dateStr ?? fallback);
  if (isNaN(d.getTime())) return '—';
  const day = d.getDate().toString().padStart(2, '0');
  const month = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = (hours % 12 || 12).toString().padStart(2, '0');
  return `${day} ${month} ${year}, ${h}:${mins} ${ampm}`;
};

const formatDuration = (seconds: number | null): string => {
  if (seconds === null || seconds === undefined) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ScriptIcon: React.FC<{ iconType: IconType }> = ({ iconType }) => {
  const { icon: Icon, bg, color } = ICON_CONFIG[iconType];
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '8px',
        bgcolor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 20, color }} />
    </Box>
  );
};

const StatusChip: React.FC<{ status: ScriptStatus }> = ({ status }) => {
  const { bg, color, icon: Icon, label } = STATUS_CONFIG[status];
  return (
    <Chip
      icon={<Icon sx={{ fontSize: '14px !important', color: `${color} !important` }} />}
      label={label}
      size="small"
      sx={{
        bgcolor: bg,
        color,
        fontWeight: 600,
        fontSize: '12px',
        height: '26px',
        border: 'none',
        '& .MuiChip-icon': { ml: '6px' },
      }}
    />
  );
};

const SortableHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, userSelect: 'none', cursor: 'default' }}>
    <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#546E7A', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <UnfoldMoreIcon sx={{ fontSize: 16, color: '#90A4AE' }} />
  </Box>
);

const CodeBlock: React.FC<{ code: string }> = ({ code }) => (
  <Box
    sx={{
      bgcolor: '#1E2832',
      borderRadius: '6px',
      p: 1.5,
      mt: 1,
      fontFamily: '"Fira Code", "Consolas", "Courier New", monospace',
      fontSize: '12px',
      color: '#E8F0FE',
      overflowX: 'auto',
      whiteSpace: 'pre',
      lineHeight: 1.7,
    }}
  >
    {code}
  </Box>
);

const StepBlock: React.FC<{ step: number; title: string; description: string; children?: React.ReactNode }> = (
  { step, title, description, children }
) => (
  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: '#E3F2FD',
        color: '#1565C0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '13px',
        mt: 0.2,
      }}
    >
      {step}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#10383D', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: '#546E7A', lineHeight: 1.65 }}>
        {description}
      </Typography>
      {children}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ScriptExecutionPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [scriptRuns, setScriptRuns] = useState<ApiScriptRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const fetchRuns = useCallback(async (query: string) => {
    setFetchError(null);
    try {
      const params = new URLSearchParams({ page: '1', page_size: '50' });
      if (query.trim()) params.append('script_name', query.trim());
      const res = await fetch(
        `${constants.API_BASE_URL}project/script-execution/runs/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getCookie('token')}` } }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json();
      setScriptRuns(json.data ?? []);
    } catch {
      setFetchError('Failed to load script runs. Please try refreshing.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Clear any leftover task-status Redux state when leaving the page
  // (covers the case where the user navigates away before closing the script run modal)
  useEffect(() => {
    return () => { dispatch(setClearSaveJsonStatus({})); };
  }, [dispatch]);

  // Debounce search query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch on mount/search change + poll every 10s for live status
  // Polling is suspended while a script execution task is in progress
  useEffect(() => {
    if (isRunning) return;
    fetchRuns(debouncedQuery);
    const intervalId = setInterval(() => fetchRuns(debouncedQuery), 10000);
    return () => clearInterval(intervalId);
  }, [debouncedQuery, fetchRuns, isRunning]);

  return (
    <Box sx={{ bgcolor: '#F7F9FA', minHeight: '100vh', width: '100%' }}>
      {/* ── Page header ── */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          px: 4,
          py: 2.5,
          borderBottom: '1px solid #E8EAEF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '24px', color: '#10383D', lineHeight: '36px' }}>
            Script Execution
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#78909C', mt: 0.25 }}>
            Run and monitor scripts. Monitor the execution status and view logs.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<MenuBookIcon sx={{ fontSize: 18 }} />}
            onClick={() => setRulesOpen(true)}
            sx={{
              height: '40px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              borderColor: '#CFD8DC',
              color: '#546E7A',
              '&:hover': { borderColor: '#90A4AE', bgcolor: '#F5F7FA' },
            }}
          >
            Rules
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              height: '40px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              background: 'linear-gradient(45deg, #478ed1, #42a5f5)',
              '&:hover': { background: 'linear-gradient(45deg, #3679b8, #2196f3)' },
            }}
          >
            New Script Run
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 4, pt: 2, pb: 4 }}>
        {/* ── Tabs ── */}
        <Tabs
          value={0}
          sx={{
            mb: 2,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '14px', minWidth: 0, px: 0, mr: 3 },
            '& .MuiTabs-indicator': { bgcolor: '#007BFF', height: '3px', borderRadius: '2px 2px 0 0' },
          }}
        >
          <Tab label="Script Runs" />
        </Tabs>

        {/* ── Table card ── */}
        <Paper sx={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          {/* Toolbar */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2.5,
              py: 1.75,
              borderBottom: '1px solid #F0F2F5',
            }}
          >
            <TextField
              size="small"
              placeholder="Search by script name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                  '&:hover fieldset': { borderColor: '#3D85C6' },
                  '&.Mui-focused fieldset': { borderColor: '#3D85C6' },
                },
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={() => fetchRuns(debouncedQuery)}
              sx={{
                height: '36px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#CFD8DC',
                color: '#546E7A',
                '&:hover': { borderColor: '#90A4AE', bgcolor: '#F5F7FA' },
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                  <TableCell sx={{ py: 1.5, pl: 2.5, borderBottom: '1px solid #F0F2F5' }}>
                    <SortableHeader label="SCRIPT NAME" />
                  </TableCell>
                  <TableCell sx={{ py: 1.5, borderBottom: '1px solid #F0F2F5' }}>
                    <SortableHeader label="STATUS" />
                  </TableCell>
                  <TableCell sx={{ py: 1.5, borderBottom: '1px solid #F0F2F5' }}>
                    <SortableHeader label="LAST RUN" />
                  </TableCell>
                  <TableCell sx={{ py: 1.5, borderBottom: '1px solid #F0F2F5' }}>
                    <SortableHeader label="DURATION" />
                  </TableCell>
                  <TableCell sx={{ py: 1.5, borderBottom: '1px solid #F0F2F5' }}>
                    <SortableHeader label="TRIGGERED BY" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && scriptRuns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#90A4AE', fontSize: '14px' }}>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : fetchError ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#C62828', fontSize: '14px' }}>
                      {fetchError}
                    </TableCell>
                  </TableRow>
                ) : scriptRuns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#90A4AE', fontSize: '14px' }}>
                      No script runs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  scriptRuns.map((run) => (
                    <TableRow
                      key={run.run_id}
                      sx={{
                        '&:hover': { bgcolor: '#F7F9FA' },
                        '& td': { borderBottom: '1px solid #F0F2F5' },
                      }}
                    >
                      {/* Script Name */}
                      <TableCell sx={{ py: 1.75, pl: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <ScriptIcon iconType={deriveIconType(run.script_name)} />
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D' }}>
                              {run.script_name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ py: 1.75 }}>
                        <StatusChip status={mapApiStatus(run.status)} />
                      </TableCell>

                      {/* Last Run */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Typography sx={{ fontSize: '13px', color: '#37474F' }}>
                          {formatRunDate(run.started_at, run.created_at)}
                        </Typography>
                      </TableCell>

                      {/* Duration */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Typography sx={{ fontSize: '13px', color: '#37474F' }}>
                          {formatDuration(run.duration_seconds)}
                        </Typography>
                      </TableCell>

                      {/* Triggered By */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Typography sx={{ fontSize: '13px', color: '#37474F' }}>{run.triggered_by_name}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* ── Script Rules dialog ── */}
      <Dialog
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#10383D', pb: 1, pr: 6 }}>
          Steps to Create a Script
          <Typography sx={{ fontSize: '13px', color: '#78909C', fontWeight: 400, mt: 0.25 }}>
            Follow these steps to create a script compatible with the Script Execution feature.
          </Typography>
          <IconButton
            onClick={() => setRulesOpen(false)}
            size="small"
            sx={{ position: 'absolute', right: 16, top: 14, color: '#78909C' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2.5, pb: 3 }}>

          <StepBlock
            step={1}
            title="Create a .py file with a descriptive name"
            description="Name it based on what the script does."
          >
            <CodeBlock code={`segregate_images_by_size.py\nsvg_metadata_extractor.py\nresize_images.py`} />
          </StepBlock>

          <StepBlock
            step={2}
            title="Add the source path variable (copy exactly)"
            description="This tells the script where your input files are located."
          >
            <CodeBlock code={`folder_path = Path(r"SOURCE_FILE_PATH")`} />
          </StepBlock>

          <StepBlock
            step={3}
            title="Define which file types to process"
            description="List the extensions your script should pick up."
          >
            <CodeBlock code={`SUPPORTED_EXTENSIONS = {".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg"}`} />
          </StepBlock>

          <StepBlock
            step={4}
            title="Add the os.walk loop to go through all files"
            description="This iterates every file in the source folder and its subfolders."
          >
            <CodeBlock
              code={`for root, _, files in os.walk(root_dir):\n    for file in files:\n        ext = os.path.splitext(file)[1].lower()\n        if ext in SUPPORTED_EXTENSIONS:\n            total_files += 1\n            file_path = os.path.join(root, file)\n            print(f"Processing [{total_files}]: {file_path}")\n            # your processing logic here`}
            />
          </StepBlock>

          <StepBlock
            step={5}
            title="Write your processing logic inside the loop"
            description="This is the core — what you want to do with each file (move, extract, convert, etc.)"
          />

          <StepBlock
            step={6}
            title="Save the result to OUTPUT_PATH"
            description="All output must go under OUTPUT_PATH."
          >
            <CodeBlock
              code={`# If saving a CSV:\ndf.to_csv("OUTPUT_PATH/result.csv", index=False)\n\n# If creating folders with files:\nos.makedirs("OUTPUT_PATH/subfolder", exist_ok=True)`}
            />
          </StepBlock>

          {/* Important note */}
          <Box
            sx={{
              bgcolor: '#FFF8E1',
              border: '1px solid #FFD54F',
              borderRadius: '8px',
              p: 2,
              mt: 0.5,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#E65100', mb: 0.75 }}>
              ⚠ Important
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#5D4037', lineHeight: 1.7 }}>
              Never change the text{' '}
              <Box component="code" sx={{ bgcolor: '#FFE082', px: 0.6, py: 0.2, borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}>
                SOURCE_FILE_PATH
              </Box>
              {' '}or{' '}
              <Box component="code" sx={{ bgcolor: '#FFE082', px: 0.6, py: 0.2, borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}>
                OUTPUT_PATH
              </Box>
              {' '}— they get replaced automatically at runtime with actual folder paths.
            </Typography>
          </Box>

        </DialogContent>
      </Dialog>

      {/* ── New Script Run dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#10383D',
            pb: 1,
            pr: 6,
          }}
        >
          Script Run
          <IconButton
            onClick={() => setDialogOpen(false)}
            size="small"
            sx={{ position: 'absolute', right: 16, top: 14, color: '#78909C' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <RunScriptForm
            onRunStarted={() => setIsRunning(true)}
            onSuccess={() => {
              setIsRunning(false);
              setDialogOpen(false);
              fetchRuns(debouncedQuery);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScriptExecutionPage;
